import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  Edit2,
  Trash2,
  User,
  Users,
  Calendar,
  UserCheck,
  UserX,
  Mail,
  Phone,
  MapPin,
  Building2,
  Shield,
  MoreVertical,
  Search,
  Filter,
  Download,
  Upload,
  Key,
  Lock,
  Eye,
  EyeOff,
  Settings,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
  orderBy,
  serverTimestamp,
  onSnapshot,
} from 'firebase/firestore';
import { createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { firestore, auth, createUserByAdmin } from '../../database/firebase';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import TeamMembersSkeleton from '../ui/TeamMembersSkeleton';
import ConfirmationModal from '../SharedFeature/ConfirmationModal';
import {
  mergePermissions,
  validatePermissions,
  DEFAULT_PERMISSIONS,
} from '../../utils/permissions';

const EnhancedTeamManager = ({ userId, isReadOnly = false }) => {
  const { currentUser } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedMemberForPermissions, setSelectedMemberForPermissions] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, memberId: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const [filterRole, setFilterRole] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [creatingAuth, setCreatingAuth] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const ROLES = [
    { value: 'admin', label: 'Admin', color: 'bg-red-100 text-red-800', icon: Shield },
    { value: 'manager', label: 'Manager', color: 'bg-blue-100 text-blue-800', icon: Building2 },
    { value: 'employee', label: 'Employee', color: 'bg-green-100 text-green-800', icon: User },
    { value: 'intern', label: 'Intern', color: 'bg-yellow-100 text-yellow-800', icon: UserCheck },
  ];

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    let unsubscribe = null;

    // Try query with orderBy first
    const tryQueryWithOrderBy = () => {
      const q = query(
        collection(firestore, 'teamMembers'),
        where('ownerId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      unsubscribe = onSnapshot(
        q,
        querySnapshot => {
          const members = [];
          querySnapshot.forEach(doc => {
            const data = doc.data();
            members.push({
              id: doc.id,
              ...data,
              // Handle Firestore timestamp conversion
              createdAt: data.createdAt?.toDate
                ? data.createdAt.toDate()
                : data.createdAt?.seconds
                  ? new Date(data.createdAt.seconds * 1000)
                  : data.createdAt || new Date(),
              updatedAt: data.updatedAt?.toDate
                ? data.updatedAt.toDate()
                : data.updatedAt?.seconds
                  ? new Date(data.updatedAt.seconds * 1000)
                  : data.updatedAt || new Date(),
            });
          });

          setTeamMembers(members);
          setLoading(false);
        },
        error => {
          // If orderBy fails due to missing index, try without orderBy
          if (error.code === 'failed-precondition' || error.message?.includes('index')) {
            console.info(
              'Index not yet created, using fallback query. Deploy indexes with: npm run deploy-indexes'
            );
            tryQueryWithoutOrderBy();
          } else {
            console.error('Error listening to team members:', error);
            toast.error('Failed to load team members: ' + (error.message || 'Unknown error'));
            setLoading(false);
          }
        }
      );
    };

    // Fallback query without orderBy
    const tryQueryWithoutOrderBy = () => {
      const simpleQ = query(collection(firestore, 'teamMembers'), where('ownerId', '==', userId));

      if (unsubscribe) unsubscribe(); // Clean up previous listener

      unsubscribe = onSnapshot(
        simpleQ,
        querySnapshot => {
          const members = [];
          querySnapshot.forEach(doc => {
            const data = doc.data();
            members.push({
              id: doc.id,
              ...data,
              createdAt: data.createdAt?.toDate
                ? data.createdAt.toDate()
                : data.createdAt?.seconds
                  ? new Date(data.createdAt.seconds * 1000)
                  : data.createdAt || new Date(),
              updatedAt: data.updatedAt?.toDate
                ? data.updatedAt.toDate()
                : data.updatedAt?.seconds
                  ? new Date(data.updatedAt.seconds * 1000)
                  : data.updatedAt || new Date(),
            });
          });

          // Sort manually by createdAt
          members.sort((a, b) => {
            const aDate = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
            const bDate = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
            return bDate.getTime() - aDate.getTime();
          });

          setTeamMembers(members);
          setLoading(false);
        },
        fallbackError => {
          console.error('Error in fallback query:', fallbackError);
          toast.error('Failed to load team members: ' + (fallbackError.message || 'Unknown error'));
          setLoading(false);
        }
      );
    };

    // Start with orderBy query
    tryQueryWithOrderBy();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [userId]);

  const createFirebaseAuthAccount = async (email, password) => {
    try {
      // Try to use Cloud Function first (preferred method - doesn't disrupt current session)
      try {
        const result = await createUserByAdmin(
          {
            email,
            password,
            displayName: email.split('@')[0],
          },
          'coach-delegated'
        );
        // Cloud Function returns { uid, email, ... } or { userId, ... }
        return result?.uid || result?.userId || result?.user?.uid;
      } catch (cloudFunctionError) {
        // Fallback to direct client-side creation if Cloud Function fails
        console.warn('Cloud Function failed, using direct creation:', cloudFunctionError);

        // Store current user to warn them about session disruption
        const currentUser = auth.currentUser;

        // Use the existing auth instance from firebase.js
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const uid = userCredential.user.uid;

        // Sign out the newly created user immediately since we only need the UID
        await signOut(auth);

        // Warn user if they were signed in
        if (currentUser) {
          toast.warning(
            'You have been signed out. Please sign in again to continue managing team members.',
            { autoClose: 5000 }
          );
        }

        return uid;
      }
    } catch (error) {
      console.error('Error creating Firebase Auth account:', error);
      throw error;
    }
  };

  const addTeamMember = async memberData => {
    try {
      setCreatingAuth(true);
      let authUid = null;
      let tempPassword = null;

      // Create Firebase Auth account if createAccount is true
      if (memberData.createAccount) {
        tempPassword = memberData.tempPassword || `Coach@${Date.now().toString().slice(-6)}`;
        try {
          authUid = await createFirebaseAuthAccount(memberData.email, tempPassword);
        } catch (authError) {
          if (authError.code === 'auth/email-already-in-use') {
            toast.error('A user with this email already exists');
            setCreatingAuth(false);
            return;
          }
          throw authError;
        }
      }

      // Merge permissions with defaults
      const permissions = memberData.permissions
        ? mergePermissions(memberData.permissions, 'coach')
        : mergePermissions(null, 'coach');

      const newMemberRef = doc(collection(firestore, 'teamMembers'));
      const memberDataWithId = {
        ...memberData,
        id: newMemberRef.id,
        ownerId: userId,
        ownerEmail: currentUser?.email,
        authUid: authUid,
        permissions: permissions,
        delegatedUser: true,
        userType: 'coach-delegated',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'active',
      };

      // Remove temporary fields
      delete memberDataWithId.createAccount;
      delete memberDataWithId.tempPassword;

      await setDoc(newMemberRef, memberDataWithId);

      // Also create entry in delegatedUsers collection for easier querying
      if (authUid) {
        await setDoc(doc(firestore, 'delegatedUsers', authUid), {
          email: memberData.email,
          name: memberData.name,
          coachId: userId,
          coachEmail: currentUser?.email,
          permissions: permissions,
          userType: 'coach-delegated',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }

      // Don't manually update state - let the real-time listener handle it
      toast.success('Team member added successfully');
      setShowAddModal(false);

      if (authUid && tempPassword) {
        setGeneratedPassword(tempPassword);
        setShowPasswordModal(true);
      }
    } catch (error) {
      console.error('Error adding team member:', error);
      toast.error('Failed to add team member: ' + (error.message || 'Unknown error'));
    } finally {
      setCreatingAuth(false);
    }
  };

  const updateTeamMember = async (memberId, updatedData) => {
    try {
      // Merge permissions if provided
      if (updatedData.permissions) {
        updatedData.permissions = mergePermissions(updatedData.permissions, 'coach');
      }

      const memberRef = doc(firestore, 'teamMembers', memberId);
      await setDoc(
        memberRef,
        {
          ...updatedData,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      // Update delegatedUsers collection if authUid exists
      const member = teamMembers.find(m => m.id === memberId);
      if (member?.authUid) {
        await setDoc(
          doc(firestore, 'delegatedUsers', member.authUid),
          {
            ...updatedData,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }

      // Don't manually update state - let the real-time listener handle it
      toast.success('Team member updated successfully');
      setShowEditModal(false);
      setEditingMember(null);
    } catch (error) {
      console.error('Error updating team member:', error);
      toast.error('Failed to update team member');
    }
  };

  const handleDeleteClick = memberId => {
    setDeleteConfirm({ isOpen: true, memberId });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.memberId) return;

    setIsDeleting(true);
    try {
      const member = teamMembers.find(m => m.id === deleteConfirm.memberId);

      // Delete from teamMembers collection
      await deleteDoc(doc(firestore, 'teamMembers', deleteConfirm.memberId));

      // Also delete from delegatedUsers collection if exists
      if (member?.authUid) {
        try {
          await deleteDoc(doc(firestore, 'delegatedUsers', member.authUid));
        } catch (error) {
          console.warn('Failed to delete from delegatedUsers:', error);
        }
      }

      // Don't manually update state - let the real-time listener handle it
      setDeleteConfirm({ isOpen: false, memberId: null });
      toast.success('Team member deleted successfully');
    } catch (error) {
      console.error('Error deleting team member:', error);
      toast.error('Failed to delete team member');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ isOpen: false, memberId: null });
  };

  const deleteTeamMember = handleDeleteClick;

  const toggleMemberStatus = async (memberId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    try {
      const memberRef = doc(firestore, 'teamMembers', memberId);
      await setDoc(
        memberRef,
        {
          status: newStatus,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      // Don't manually update state - let the real-time listener handle it
      toast.success(
        `Team member ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`
      );
    } catch (error) {
      toast.error('Failed to update member status');
    }
  };

  const filteredMembers = teamMembers.filter(member => {
    const matchesSearch =
      member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.position?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRole = filterRole === 'all' || member.role === filterRole;

    return matchesSearch && matchesRole;
  });

  const getRoleInfo = role => {
    return ROLES.find(r => r.value === role) || ROLES[2];
  };

  const getStatusColor = status => {
    return status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';
  };

  const EmptyState = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center justify-center px-4 py-16"
    >
      <div className="mb-6 rounded-full bg-slate-100 p-6">
        <Users className="h-12 w-12 text-slate-400" />
      </div>
      <h3 className="mb-2 text-xl font-semibold text-slate-900">No team members yet</h3>
      <p className="mb-6 max-w-md text-center text-slate-600">
        Start building your team by adding employees, managers, and other team members. You can
        manage their roles, contact information, and permissions.
      </p>
      {!isReadOnly && (
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center space-x-2 rounded-xl bg-blue-600 px-6 py-3 font-medium text-white transition-colors hover:bg-blue-700"
        >
          <Plus className="h-5 w-5" />
          <span>Add First Team Member</span>
        </button>
      )}
    </motion.div>
  );

  if (loading) {
    return <TeamMembersSkeleton />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Team Management</h2>
          <p className="mt-1 text-slate-600">Manage your team members, roles, and permissions</p>
        </div>

        {!isReadOnly && (
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center space-x-2 rounded-xl bg-blue-600 px-4 py-2 font-medium text-white transition-colors hover:bg-blue-700"
          >
            <Plus className="h-5 w-5" />
            <span>Add Member</span>
          </button>
        )}
      </div>

      {teamMembers.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-xl border bg-white p-4">
            <div className="flex items-center">
              <div className="rounded-lg bg-blue-100 p-2">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-slate-600">Total Members</p>
                <p className="text-2xl font-bold text-slate-900">{teamMembers.length}</p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border bg-white p-4">
            <div className="flex items-center">
              <div className="rounded-lg bg-green-100 p-2">
                <UserCheck className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-slate-600">Active</p>
                <p className="text-2xl font-bold text-slate-900">
                  {teamMembers.filter(m => m.status === 'active').length}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border bg-white p-4">
            <div className="flex items-center">
              <div className="rounded-lg bg-red-100 p-2">
                <Shield className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-slate-600">Admins</p>
                <p className="text-2xl font-bold text-slate-900">
                  {teamMembers.filter(m => m.role === 'admin').length}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border bg-white p-4">
            <div className="flex items-center">
              <div className="rounded-lg bg-yellow-100 p-2">
                <Building2 className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-slate-600">Managers</p>
                <p className="text-2xl font-bold text-slate-900">
                  {teamMembers.filter(m => m.role === 'manager').length}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {teamMembers.length > 0 && (
        <div className="rounded-xl border bg-white p-4">
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-slate-400" />
              <input
                type="text"
                placeholder="Search team members..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full rounded-lg border py-2 pl-10 pr-4 focus:border-transparent focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <select
              value={filterRole}
              onChange={e => setFilterRole(e.target.value)}
              className="rounded-lg border px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Roles</option>
              {ROLES.map(role => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>

            <div className="flex rounded-lg border">
              <button
                onClick={() => setViewMode('grid')}
                className={`rounded-l-lg px-3 py-2 text-sm font-medium ${
                  viewMode === 'grid'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                Grid
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`rounded-r-lg px-3 py-2 text-sm font-medium ${
                  viewMode === 'list'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                List
              </button>
            </div>
          </div>
        </div>
      )}

      {filteredMembers.length === 0 ? (
        teamMembers.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="py-16 text-center">
            <p className="text-slate-600">No team members match your search criteria.</p>
          </div>
        )
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredMembers.map(member => (
            <TeamMemberCard
              key={member.id}
              member={member}
              onEdit={() => {
                setEditingMember(member);
                setShowEditModal(true);
              }}
              onPermissions={() => {
                setSelectedMemberForPermissions(member);
                setShowPermissionsModal(true);
              }}
              onDelete={() => deleteTeamMember(member.id)}
              onToggleStatus={() => toggleMemberStatus(member.id, member.status)}
              isReadOnly={isReadOnly}
              getRoleInfo={getRoleInfo}
              getStatusColor={getStatusColor}
            />
          ))}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border bg-white">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                    Member
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                    Joined
                  </th>
                  {!isReadOnly && (
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredMembers.map(member => (
                  <TeamMemberRow
                    key={member.id}
                    member={member}
                    onEdit={() => {
                      setEditingMember(member);
                      setShowEditModal(true);
                    }}
                    onPermissions={() => {
                      setSelectedMemberForPermissions(member);
                      setShowPermissionsModal(true);
                    }}
                    onDelete={() => deleteTeamMember(member.id)}
                    onToggleStatus={() => toggleMemberStatus(member.id, member.status)}
                    isReadOnly={isReadOnly}
                    getRoleInfo={getRoleInfo}
                    getStatusColor={getStatusColor}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <AnimatePresence>
        {showAddModal && (
          <TeamMemberModal
            isOpen={showAddModal}
            onClose={() => setShowAddModal(false)}
            onSave={addTeamMember}
            roles={ROLES}
            title="Add Team Member"
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showEditModal && editingMember && (
          <TeamMemberModal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setEditingMember(null);
            }}
            onSave={data => updateTeamMember(editingMember.id, data)}
            initialData={editingMember}
            roles={ROLES}
            title="Edit Team Member"
          />
        )}
      </AnimatePresence>

      <ConfirmationModal
        isOpen={deleteConfirm.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Team Member"
        message="Are you sure you want to delete this team member? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonColor="bg-red-600 hover:bg-red-700"
        isLoading={isDeleting}
      />

      {/* Password Display Modal */}
      <AnimatePresence>
        {showPasswordModal && generatedPassword && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
            onClick={() => setShowPasswordModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-900">Account Created Successfully</h3>
                <button
                  onClick={() => setShowPasswordModal(false)}
                  className="rounded-lg p-1 text-slate-400 hover:bg-slate-100"
                >
                  <XCircle className="h-5 w-5" />
                </button>
              </div>
              <div className="mb-4 rounded-lg bg-blue-50 p-4">
                <p className="mb-2 text-sm font-medium text-slate-700">
                  Temporary password for login:
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 rounded bg-white px-3 py-2 font-mono text-lg font-bold text-blue-600">
                    {generatedPassword}
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(generatedPassword);
                      toast.success('Password copied to clipboard');
                    }}
                    className="rounded-lg bg-blue-600 p-2 text-white hover:bg-blue-700"
                  >
                    <Key className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <p className="mb-4 text-sm text-slate-600">
                Share this password with the team member. They should change it after first login.
              </p>
              <button
                onClick={() => setShowPasswordModal(false)}
                className="w-full rounded-lg bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-700"
              >
                Got it
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Permissions Configuration Modal */}
      <AnimatePresence>
        {showPermissionsModal && selectedMemberForPermissions && (
          <PermissionsModal
            member={selectedMemberForPermissions}
            onClose={() => {
              setShowPermissionsModal(false);
              setSelectedMemberForPermissions(null);
            }}
            onSave={permissions => {
              updateTeamMember(selectedMemberForPermissions.id, { permissions });
              setShowPermissionsModal(false);
              setSelectedMemberForPermissions(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const TeamMemberCard = ({
  member,
  onEdit,
  onDelete,
  onToggleStatus,
  onPermissions,
  isReadOnly,
  getRoleInfo,
  getStatusColor,
}) => {
  const roleInfo = getRoleInfo(member.role);
  const RoleIcon = roleInfo.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border bg-white p-4 transition-shadow hover:shadow-lg"
    >
      <div className="mb-3 flex items-start justify-between">
        <div className="flex items-center space-x-3">
          <div className="rounded-full bg-slate-100 p-2">
            <User className="h-6 w-6 text-slate-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">{member.name || 'No Name'}</h3>
            <p className="text-sm text-slate-600">{member.position || 'No Position'}</p>
          </div>
        </div>

        {!isReadOnly && (
          <div className="relative">
            <button className="p-1 text-slate-400 hover:text-slate-600">
              <MoreVertical className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      <div className="mb-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-600">Role</span>
          <span
            className={`inline-flex items-center space-x-1 rounded-full px-2 py-1 text-xs font-medium ${roleInfo.color}`}
          >
            <RoleIcon className="h-3 w-3" />
            <span>{roleInfo.label}</span>
          </span>
        </div>

        {member.delegatedUser && member.authUid && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">Account Status</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
              <CheckCircle className="h-3 w-3" />
              Has Login Account
            </span>
          </div>
        )}

        {member.email && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">Email</span>
            <span className="flex items-center text-sm text-slate-900">
              <Mail className="mr-1 h-3 w-3" />
              {member.email}
            </span>
          </div>
        )}

        {member.phone && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">Phone</span>
            <span className="flex items-center text-sm text-slate-900">
              <Phone className="mr-1 h-3 w-3" />
              {member.phone}
            </span>
          </div>
        )}

        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-600">Status</span>
          <span
            className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(member.status)}`}
          >
            {member.status === 'active' ? (
              <UserCheck className="mr-1 h-3 w-3" />
            ) : (
              <UserX className="mr-1 h-3 w-3" />
            )}
            {member.status}
          </span>
        </div>
      </div>

      {!isReadOnly && (
        <div className="flex flex-wrap gap-2">
          {member.delegatedUser && member.authUid && onPermissions && (
            <button
              onClick={onPermissions}
              className="flex items-center justify-center gap-1 rounded-lg bg-purple-50 px-3 py-2 text-sm font-medium text-purple-600 transition-colors hover:bg-purple-100"
              title="Manage Permissions"
            >
              <Settings className="h-4 w-4" />
              <span className="hidden sm:inline">Permissions</span>
            </button>
          )}
          <button
            onClick={onEdit}
            className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-blue-50 px-3 py-2 text-sm font-medium text-blue-600 transition-colors hover:bg-blue-100"
          >
            <Edit2 className="h-4 w-4" />
            <span className="hidden sm:inline">Edit</span>
          </button>
          <button
            onClick={onToggleStatus}
            className={`flex flex-1 items-center justify-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
              member.status === 'active'
                ? 'bg-orange-50 text-orange-600 hover:bg-orange-100'
                : 'bg-green-50 text-green-600 hover:bg-green-100'
            }`}
          >
            {member.status === 'active' ? (
              <>
                <UserX className="h-4 w-4" />
                <span className="hidden sm:inline">Deactivate</span>
              </>
            ) : (
              <>
                <UserCheck className="h-4 w-4" />
                <span className="hidden sm:inline">Activate</span>
              </>
            )}
          </button>
          <button
            onClick={onDelete}
            className="flex items-center justify-center rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-100"
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )}
    </motion.div>
  );
};

const TeamMemberRow = ({
  member,
  onEdit,
  onDelete,
  onToggleStatus,
  onPermissions,
  isReadOnly,
  getRoleInfo,
  getStatusColor,
}) => {
  const roleInfo = getRoleInfo(member.role);
  const RoleIcon = roleInfo.icon;

  return (
    <tr className="hover:bg-slate-50">
      <td className="whitespace-nowrap px-6 py-4">
        <div className="flex items-center">
          <div className="mr-3 rounded-full bg-slate-100 p-2">
            <User className="h-5 w-5 text-slate-600" />
          </div>
          <div>
            <div className="text-sm font-medium text-slate-900">{member.name || 'No Name'}</div>
            <div className="text-sm text-slate-500">{member.position || 'No Position'}</div>
          </div>
        </div>
      </td>
      <td className="whitespace-nowrap px-6 py-4">
        <span
          className={`inline-flex items-center space-x-1 rounded-full px-2 py-1 text-xs font-medium ${roleInfo.color}`}
        >
          <RoleIcon className="h-3 w-3" />
          <span>{roleInfo.label}</span>
        </span>
      </td>
      <td className="whitespace-nowrap px-6 py-4">
        <div className="text-sm text-slate-900">{member.email || 'No email'}</div>
        {member.phone && <div className="text-sm text-slate-500">{member.phone}</div>}
      </td>
      <td className="whitespace-nowrap px-6 py-4">
        <span
          className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(member.status)}`}
        >
          {member.status === 'active' ? (
            <UserCheck className="mr-1 h-3 w-3" />
          ) : (
            <UserX className="mr-1 h-3 w-3" />
          )}
          {member.status}
        </span>
      </td>
      <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-500">
        {member.createdAt
          ? member.createdAt instanceof Date
            ? member.createdAt.toLocaleDateString()
            : member.createdAt?.seconds
              ? new Date(member.createdAt.seconds * 1000).toLocaleDateString()
              : new Date(member.createdAt).toLocaleDateString()
          : 'Unknown'}
      </td>
      {!isReadOnly && (
        <td className="whitespace-nowrap px-6 py-4 text-sm font-medium">
          <div className="flex flex-wrap gap-2">
            {member.delegatedUser && member.authUid && onPermissions && (
              <button
                onClick={onPermissions}
                className="inline-flex items-center gap-1 rounded-lg bg-purple-50 px-2 py-1 text-xs font-medium text-purple-600 hover:bg-purple-100"
                title="Manage Permissions"
              >
                <Settings className="h-3 w-3" />
                Permissions
              </button>
            )}
            <button onClick={onEdit} className="text-blue-600 hover:text-blue-900">
              Edit
            </button>
            <button
              onClick={onToggleStatus}
              className={
                member.status === 'active'
                  ? 'text-orange-600 hover:text-orange-900'
                  : 'text-green-600 hover:text-green-900'
              }
            >
              {member.status === 'active' ? 'Deactivate' : 'Activate'}
            </button>
            <button onClick={onDelete} className="text-red-600 hover:text-red-900">
              Delete
            </button>
          </div>
        </td>
      )}
    </tr>
  );
};

const TeamMemberModal = ({ isOpen, onClose, onSave, initialData, roles, title }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    role: 'employee',
    department: '',
    location: '',
    status: 'active',
    createAccount: false,
    permissions: null,
    ...initialData,
  });
  const [loading, setLoading] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // For multi-step form

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);

    try {
      await onSave(formData);
    } catch (error) {
      toast.error('Error saving team member:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!isOpen) return null;

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
            onClick={onClose}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="relative bg-blue-600 px-6 py-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-white">{title}</h3>
                    <p className="mt-1 text-sm text-blue-100">
                      {initialData
                        ? 'Update team member details'
                        : 'Add a new team member to your organization'}
                    </p>
                  </div>
                  <button
                    onClick={onClose}
                    className="rounded-lg p-2 text-white/90 transition-all hover:bg-white/20 hover:text-white"
                  >
                    <XCircle className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Form Content */}
              <div
                className="overflow-y-auto px-6 py-6"
                style={{ maxHeight: 'calc(90vh - 200px)' }}
              >
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Basic Information Section */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 border-b border-gray-200 pb-2">
                      <User className="h-5 w-5 text-blue-600" />
                      <h4 className="text-lg font-semibold text-gray-900">Basic Information</h4>
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <label className="flex items-center gap-1 text-sm font-semibold text-gray-700">
                          <User className="h-4 w-4 text-gray-400" />
                          Full Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={e => handleChange('name', e.target.value)}
                          className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 transition-all placeholder:text-gray-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          placeholder="John Doe"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="flex items-center gap-1 text-sm font-semibold text-gray-700">
                          <Mail className="h-4 w-4 text-gray-400" />
                          Email Address <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          required
                          value={formData.email}
                          onChange={e => handleChange('email', e.target.value)}
                          className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 transition-all placeholder:text-gray-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          placeholder="john.doe@example.com"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="flex items-center gap-1 text-sm font-semibold text-gray-700">
                          <Phone className="h-4 w-4 text-gray-400" />
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={e => handleChange('phone', e.target.value)}
                          className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 transition-all placeholder:text-gray-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          placeholder="+1 (555) 000-0000"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="flex items-center gap-1 text-sm font-semibold text-gray-700">
                          <Building2 className="h-4 w-4 text-gray-400" />
                          Position
                        </label>
                        <input
                          type="text"
                          value={formData.position}
                          onChange={e => handleChange('position', e.target.value)}
                          className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 transition-all placeholder:text-gray-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          placeholder="Senior Developer"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="flex items-center gap-1 text-sm font-semibold text-gray-700">
                          <Shield className="h-4 w-4 text-gray-400" />
                          Role <span className="text-red-500">*</span>
                        </label>
                        <select
                          required
                          value={formData.role}
                          onChange={e => handleChange('role', e.target.value)}
                          className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 text-gray-900 transition-all focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                        >
                          {roles.map(role => {
                            const RoleIcon = role.icon;
                            return (
                              <option key={role.value} value={role.value}>
                                {role.label}
                              </option>
                            );
                          })}
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="flex items-center gap-1 text-sm font-semibold text-gray-700">
                          <Building2 className="h-4 w-4 text-gray-400" />
                          Department
                        </label>
                        <input
                          type="text"
                          value={formData.department}
                          onChange={e => handleChange('department', e.target.value)}
                          className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 transition-all placeholder:text-gray-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          placeholder="Engineering"
                        />
                      </div>

                      <div className="space-y-2 md:col-span-2">
                        <label className="flex items-center gap-1 text-sm font-semibold text-gray-700">
                          <MapPin className="h-4 w-4 text-gray-400" />
                          Location
                        </label>
                        <input
                          type="text"
                          value={formData.location}
                          onChange={e => handleChange('location', e.target.value)}
                          className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 transition-all placeholder:text-gray-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          placeholder="New York, NY"
                        />
                      </div>

                      {initialData && (
                        <div className="space-y-2">
                          <label className="flex items-center gap-1 text-sm font-semibold text-gray-700">
                            <UserCheck className="h-4 w-4 text-gray-400" />
                            Status
                          </label>
                          <select
                            value={formData.status}
                            onChange={e => handleChange('status', e.target.value)}
                            className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 text-gray-900 transition-all focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Account Creation Section */}
                  {!initialData && (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 border-b border-gray-200 pb-2">
                        <Key className="h-5 w-5 text-purple-600" />
                        <h4 className="text-lg font-semibold text-gray-900">Account Access</h4>
                      </div>

                      <div className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-5 transition-all hover:border-gray-400">
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                              <input
                                type="checkbox"
                                id="createAccount"
                                checked={formData.createAccount}
                                onChange={e => {
                                  handleChange('createAccount', e.target.checked);
                                  if (e.target.checked) {
                                    handleChange('permissions', mergePermissions(null, 'coach'));
                                  } else {
                                    handleChange('permissions', null);
                                  }
                                }}
                                className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                              />
                            </div>
                          </div>
                          <div className="flex-1">
                            <label htmlFor="createAccount" className="block cursor-pointer">
                              <div className="flex items-center gap-2">
                                <span className="text-base font-bold text-gray-900">
                                  Create Login Account
                                </span>
                                {formData.createAccount && (
                                  <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                                    Enabled
                                  </span>
                                )}
                              </div>
                              <p className="mt-1 text-sm text-gray-600">
                                Enable this user to sign in independently with their own
                                credentials. They will access the coach dashboard with restricted
                                permissions you configure.
                              </p>
                            </label>
                            {formData.createAccount && (
                              <motion.button
                                type="button"
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                onClick={() => setShowPermissionsModal(true)}
                                className="mt-4 flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:bg-blue-700 hover:shadow-lg"
                              >
                                <Settings className="h-4 w-4" />
                                Configure Permissions
                                {formData.permissions && (
                                  <span className="ml-1 rounded-full bg-white/20 px-2 py-0.5 text-xs">
                                    {
                                      Object.keys(formData.permissions).filter(
                                        k => k !== 'enabled' && formData.permissions[k]?.view
                                      ).length
                                    }{' '}
                                    sections
                                  </span>
                                )}
                              </motion.button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center justify-end gap-3 border-t border-gray-200 pt-6">
                    <button
                      type="button"
                      onClick={onClose}
                      className="rounded-xl border-2 border-gray-300 bg-white px-6 py-3 font-semibold text-gray-700 transition-all hover:border-gray-400 hover:bg-gray-50 disabled:opacity-50"
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="rounded-lg bg-blue-600 px-8 py-3 font-semibold text-white shadow-md transition-all hover:bg-blue-700 hover:shadow-lg disabled:opacity-50 disabled:hover:bg-blue-600"
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                            className="h-4 w-4 rounded-full border-2 border-white border-t-transparent"
                          />
                          Saving...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <UserCheck className="h-4 w-4" />
                          {initialData ? 'Update Member' : 'Add Member'}
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Permissions Modal Integration */}
      <AnimatePresence>
        {showPermissionsModal && (
          <PermissionsModal
            member={{ ...formData, name: formData.name || 'New Member' }}
            onClose={() => setShowPermissionsModal(false)}
            onSave={permissions => {
              handleChange('permissions', permissions);
              setShowPermissionsModal(false);
              toast.success('Permissions configured successfully');
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
};

// Permissions Configuration Modal
const PermissionsModal = ({ member, onClose, onSave }) => {
  const [permissions, setPermissions] = useState(
    member.permissions || mergePermissions(null, 'coach')
  );
  const [expandedCategories, setExpandedCategories] = useState({});

  const toggleCategory = category => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const togglePermission = (category, action) => {
    setPermissions(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [action]: !prev[category]?.[action],
      },
    }));
  };

  const toggleCategoryAll = (category, value) => {
    const categoryPerms = permissions[category] || {};
    const newCategoryPerms = {};
    Object.keys(categoryPerms).forEach(action => {
      newCategoryPerms[action] = value;
    });
    setPermissions(prev => ({
      ...prev,
      [category]: newCategoryPerms,
    }));
  };

  const handleSave = () => {
    const validation = validatePermissions(permissions);
    if (!validation.valid) {
      toast.error('Invalid permissions: ' + validation.errors.join(', '));
      return;
    }
    onSave(permissions);
  };

  const permissionCategories = [
    {
      id: 'sessions',
      label: 'Sessions',
      icon: Calendar,
      actions: ['view', 'create', 'edit', 'delete', 'manageCalendar'],
    },
    {
      id: 'clients',
      label: 'Clients',
      icon: Users,
      actions: ['view', 'create', 'edit', 'delete', 'assign'],
    },
    {
      id: 'resources',
      label: 'Resources',
      icon: Upload,
      actions: [
        'view',
        'create',
        'edit',
        'delete',
        'templates',
        'knowledgeBase',
        'bestPractices',
        'coachingTools',
      ],
    },
    {
      id: 'crm',
      label: 'CRM',
      icon: Building2,
      actions: ['view', 'create', 'edit', 'delete', 'notes', 'assessments', 'tasks'],
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: Download,
      actions: ['view', 'create', 'export'],
    },
    {
      id: 'team',
      label: 'Team Management',
      icon: User,
      actions: ['view', 'manage'],
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: Settings,
      actions: ['view', 'edit'],
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-slate-900">Configure Permissions</h3>
            <p className="mt-1 text-sm text-slate-600">
              Set what {member.name} can view and manage in the coach dashboard
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <XCircle className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-4 flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="enablePermissions"
              checked={permissions.enabled !== false}
              onChange={e => setPermissions(prev => ({ ...prev, enabled: e.target.checked }))}
              className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="enablePermissions" className="text-sm font-semibold text-slate-900">
              Enable Access Control
            </label>
          </div>
          <span
            className={`rounded-full px-3 py-1 text-xs font-medium ${
              permissions.enabled !== false
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}
          >
            {permissions.enabled !== false ? 'Enabled' : 'Disabled'}
          </span>
        </div>

        {permissions.enabled !== false && (
          <div className="space-y-3">
            {permissionCategories.map(category => {
              const CategoryIcon = category.icon;
              const categoryPerms = permissions[category.id] || {};
              const allChecked = category.actions.every(action => categoryPerms[action] === true);
              const someChecked = category.actions.some(action => categoryPerms[action] === true);
              const isExpanded = expandedCategories[category.id];

              return (
                <div
                  key={category.id}
                  className="rounded-lg border border-gray-200 bg-white transition-all hover:border-blue-300"
                >
                  <button
                    type="button"
                    onClick={() => toggleCategory(category.id)}
                    className="flex w-full items-center justify-between p-4 text-left"
                  >
                    <div className="flex items-center gap-3">
                      <CategoryIcon className="h-5 w-5 text-slate-600" />
                      <span className="font-semibold text-slate-900">{category.label}</span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          allChecked
                            ? 'bg-green-100 text-green-800'
                            : someChecked
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {category.actions.filter(a => categoryPerms[a]).length}/
                        {category.actions.length}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={allChecked}
                        ref={input => {
                          if (input) input.indeterminate = someChecked && !allChecked;
                        }}
                        onChange={e => {
                          e.stopPropagation();
                          toggleCategoryAll(category.id, e.target.checked);
                        }}
                        onClick={e => e.stopPropagation()}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      {isExpanded ? (
                        <EyeOff className="h-5 w-5 text-slate-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-slate-400" />
                      )}
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="border-t border-gray-200 p-4">
                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                        {category.actions.map(action => (
                          <label
                            key={action}
                            className="flex items-center gap-2 rounded-lg border border-gray-200 p-2 hover:bg-gray-50"
                          >
                            <input
                              type="checkbox"
                              checked={categoryPerms[action] === true}
                              onChange={() => togglePermission(category.id, action)}
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm font-medium capitalize text-slate-700">
                              {action.replace(/([A-Z])/g, ' $1').trim()}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3 border-t pt-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 font-medium text-slate-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="rounded-lg bg-blue-600 px-6 py-2 font-medium text-white hover:bg-blue-700"
          >
            Save Permissions
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default EnhancedTeamManager;
